/* eslint-disable @typescript-eslint/consistent-type-assertions */

import { useDocumentVisibility } from '@vueuse/core'
import debug from '../../utils/console'
import type {
  AssertiveLogImpressionEvent,
  AssertiveYieldOptions,
  AyData,
  AyLogImpressionEvent,
} from '../../types/assertive'

const logger = debug.extend('composables:useAssertiveYield')

export function useAssertiveYield(options?: Partial<AssertiveYieldOptions>) {
  const { $ayLoaded } = useNuxtApp()
  if (!import.meta.client || !$ayLoaded)
    return {
      assertive: null,
      ayManagerEnv: null,
      ayData: null,
      initAY: () => Promise.resolve(),
      unMountSlotTargeting: () => Promise.resolve(),
    }

  const previousCalledAction = ref<string | null>(null)

  window.addEventListener(
    'assertive_logImpression',
    async (e: AssertiveLogImpressionEvent) => {
      try {
        const payload = e?.data?.payload as AyLogImpressionEvent

        const { unfilled, auctionId } = payload
        const isNewRequest = previousCalledAction.value !== auctionId

        if (options?.onAdFulfilled && !unfilled && auctionId && isNewRequest) {
          previousCalledAction.value = auctionId
          await options?.onAdFulfilled(payload)
        }
      } catch (e) {
        logger.error(e)
      }
    }
  )

  const pageVisibility = useDocumentVisibility()
  watch(pageVisibility, (value) => {
    const isPageVisible = value === 'visible'
    window.isPageVisible = isPageVisible

    if (isPageVisible) {
      window.ayManagerEnv.cmd.push(() => {
        window.ayManagerEnv.changePage()
        window.ayManagerEnv.dispatchManualEvent()
      })
    }
  })

  const ayData: AyData = {
    version: window.ayManagerEnv?.versionInfo?.versionId,
    deployment: window.ayManagerEnv?.versionInfo?.deployId,
    deploymentPercentage: window.ayManagerEnv?.versionInfo?.percentage || 0,
  }

  return {
    assertive: window.assertive,
    ayManagerEnv: window.ayManagerEnv,
    ayData,
  }
}

/**
 * Assertive Yield only looks for divs when it first loads,
 * for SPAs or elements that interactively change, we need to reevaluate the page
 * using the `changePage` and `dispatchManualEvent` functions suggested from
 * their docs.
 **/
export function useAssertiveYieldReevaluate(options?: AssertiveYieldOptions) {
  if (!window.ayManagerEnv) {
    useAssertiveYield(options)
  }

  window?.ayManagerEnv?.cmd?.push(() => {
    window.ayManagerEnv.changePage()
  })
}

export const logAdImpressionToAY = (
  data: Partial<AyLogImpressionEvent> | null
) => {
  if (!import.meta.client || !data) return

  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (
      window.assertive &&
      typeof window.assertive.logCustomImpression === 'function'
    ) {
      window.assertive.logCustomImpression(data)
    }
  })
}

export const logAdClickToAY = (slotId: string) => {
  if (!import.meta.client || !slotId) return

  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (
      window.assertive &&
      typeof window.assertive.logCustomClick === 'function'
    ) {
      window.assertive.logCustomClick(slotId)
    }
  })
}

export const updateUserState = (value: boolean) => {
  if (!import.meta.client) return

  window.assertiveQueue = window.assertiveQueue || []
  window.assertiveQueue.push(function () {
    if (window.assertiveYieldAnalytics) {
      window.assertiveYieldAnalytics.custom.userState = value
        ? 'isEngaged'
        : 'notEngaged'
    }
    if (window.assertive?.analytics?.custom) {
      window.assertive.analytics.custom.userState = value
        ? 'isEngaged'
        : 'notEngaged'
    }
  })
}

export default useAssertiveYield
